/*
 * AICO Personalisation Tool — independent configurator engine (homepage).
 * Binds to sections/aico-perso-tool.liquid. No dependency on personalisation-script.min.js
 * or product-form-script.min.js, and no license gate.
 *
 * Uses the SVZ model (same as the PDP): every printable thing is a stock-tracked SKU; print
 * Farbe / name size / number size / badge colour are derived from the print_rules metaobject
 * for the SELECTED jersey's type + variant + size. The jersey picker (type/variant/size) stays
 * server-rendered; the Print + Badge controls are built here from the [data-aico-svz-home] island.
 *
 * Cart bundle contract preserved (so sections/cart-template.liquid grouping keeps working):
 *   items: [ ...childOptionItems(_bundleRole:'child'), parentJersey(last, _bundleRole:'parent') ]
 *   sharing one _bundleId; personalisation values ride as line-item properties.
 */
(function () {
  'use strict';
  var CFG = window.aicoPerso || {};

  // native fetch from a fresh iframe - the page's ajax-cart wrapper patches window.fetch and
  // re-serialises our multipart to JSON (image Blob -> "{}"); the iframe's fetch is untouched.
  // Fallback: XHR transport (immune to the fetch wrapper) when iframe fetch is unavailable
  // (Safari WKWebView in the club app blocks cross-origin iframe access).
  var _nativeFetch;
  function xhrFetch(url, opts) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open(opts.method || 'GET', url);
      var h = opts.headers || {};
      Object.keys(h).forEach(function (k) { xhr.setRequestHeader(k, h[k]); });
      xhr.onload = function () { resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json: function () { return Promise.resolve(JSON.parse(xhr.responseText)); } }); };
      xhr.onerror = function () { reject(new Error('XHR failed')); };
      xhr.send(opts.body || null);
    });
  }
  function nativeFetch() {
    if (_nativeFetch) return _nativeFetch;
    try {
      var ifr = document.createElement('iframe');
      ifr.style.display = 'none'; ifr.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(ifr);
      var f = ifr.contentWindow && ifr.contentWindow.fetch;
      if (f) _nativeFetch = f.bind(ifr.contentWindow);
    } catch (e) {}
    return _nativeFetch || (_nativeFetch = xhrFetch);
  }

  var RX = { name: /spieler/i, digit: /ziffer|nummer|number/i, size: /gr.?sse|size/i, color: /farbe|colou?r/i };
  var DIVIDE_ICON = '<svg class="icon icon-divide-circle" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 12C7 11.4477 7.44772 11 8 11H16C16.5523 11 17 11.4477 17 12C17 12.5523 16.5523 13 16 13H8C7.44772 13 7 12.5523 7 12Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3ZM1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12Z" fill="currentColor"/></svg>';

  function arr(v) { return Array.isArray(v) ? v : (v == null || v === '' ? [] : [v]); }
  function listHas(list, val) { return arr(list).map(String).indexOf(String(val)) !== -1; }
  function el(tag, cls) { var n = document.createElement(tag); if (cls) n.className = cls; return n; }
  function txt(n) { return n ? (n.textContent || '').trim() : ''; }

  function formatMoney(cents) {
    var format = CFG.moneyFormat || '${{amount}}';
    cents = parseInt(cents, 10) || 0;
    var rx = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(num, p, t, d) {
      p = p == null ? 2 : p; t = t || "'"; d = d || '.';
      var n = (num / 100).toFixed(p), parts = n.split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t) + (parts[1] ? d + parts[1] : '');
    }
    var m = format.match(rx), val;
    switch (m && m[1]) {
      case 'amount_no_decimals': val = fmt(cents, 0); break;
      case 'amount_with_comma_separator': val = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': val = fmt(cents, 0, '.', ','); break;
      default: val = fmt(cents, 2);
    }
    return format.replace(rx, val);
  }

  /* find the variant whose option values contain ALL required values */
  function findVariant(print, required) {
    return (print.variants || []).find(function (v) {
      var opts = (v.options || []).map(String);
      return required.every(function (val) { return val != null && opts.indexOf(String(val)) !== -1; });
    });
  }

  /* ===================================================================== */
  function AicoPerso(root) {
    this.root = root;
    this.form = root.querySelector('[data-aico-perso-form], form[data-AddToCartForm], form');
    var dataEl = root.querySelector('[data-products]');
    this.products = dataEl ? JSON.parse(dataEl.textContent) : [];
    var svzEl = root.querySelector('[data-aico-svz-home]');
    this.data = svzEl ? JSON.parse(svzEl.textContent) : { rules: [], prints: [], players: [] };
    this.controls = root.querySelector('[data-aico-svz-controls]');

    this.state = { type: null, gender: null, variantId: null, mode: 'none', name: null, custom: '', number: null, badge: null };
    this.inCart = {};
    this._debug = (window.aicoSvzDebug === true) || /[?&]aicoSvzDebug\b/.test(window.location.search);
    this.init();
  }

  AicoPerso.prototype.init = function () {
    var self = this, root = this.root;
    var first = this.products[0];
    if (first) { this.state.type = first.type; this.state.gender = first.genderVariant; }

    // delegated interactions for the SERVER-RENDERED jersey picker (type / gender / size).
    // The SVZ controls attach their own listeners, so they intentionally do NOT use .aico-js-option.
    root.addEventListener('click', function (e) {
      var v = e.target.closest('.aico-js-option');
      if (v && root.contains(v) && !v.classList.contains('aico-option__value--disabled')) {
        e.preventDefault(); self.onOption(v); return;
      }
      var flip = e.target.closest('[data-toggle]');
      if (flip && root.contains(flip)) { e.preventDefault(); self.flip(); }
    });
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var v = e.target.closest('.aico-js-option');
      if (v && !v.classList.contains('aico-option__value--disabled')) { e.preventDefault(); self.onOption(v); }
    });

    if (this.form) this.form.addEventListener('submit', function (e) { self.onSubmit(e); });

    // load the cart first so availability = inventory - what's already in the cart
    this.loadCart(function () { self.onProductChange(/*initial*/ true); self.scaleNames(); });
    window.addEventListener('resize', function () { self.scaleNames(); });
  };

  AicoPerso.prototype.scaleNames = function () {
    var inner = this.root.querySelector('.aico-perso__media-inner');
    if (!inner) return;
    var w = inner.offsetWidth;
    if (w <= 0) return;
    var r = 500;
    var factor = w < r ? Math.max(0.7, w / r) : Math.min(1.3, 1 + (w - r) / r * 0.3);
    Array.prototype.forEach.call(this.root.querySelectorAll('.aico-perso__media-block'), function (b) {
      var base = parseFloat(b.dataset.fontSize);
      if (!base || isNaN(base)) return;
      b.style.setProperty('--font-size', (base * factor / 10).toFixed(3) + 'rem');
    });
  };

  AicoPerso.prototype.loadCart = function (cb) {
    var self = this;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { var m = {}; (c.items || []).forEach(function (i) { m[i.variant_id] = (m[i.variant_id] || 0) + (i.quantity || 0); }); self.inCart = m; })
      .catch(function () { self.inCart = self.inCart || {}; })
      .finally(function () { if (cb) cb(); });
  };

  /* ---------------- jersey selection ---------------- */
  AicoPerso.prototype.currentProduct = function () {
    var s = this.state;
    return this.products.find(function (p) { return p.type === s.type && p.genderVariant === s.gender; })
      || this.products.find(function (p) { return p.type === s.type; })
      || this.products[0] || {};
  };
  AicoPerso.prototype.variantById = function (id) {
    var p = this.currentProduct();
    return (p.variants || []).find(function (v) { return String(v.id) === String(id); });
  };
  // the selected jersey {type, variant} the SVZ rules derive from. type/genderVariant already come
  // from aico.jersey_type / aico.jersey_variant (with tool.* fallback) in the section's data-products.
  AicoPerso.prototype.jersey = function () { var p = this.currentProduct(); return { type: p.type, variant: p.genderVariant }; };
  AicoPerso.prototype.chosenSize = function () { var v = this.variantById(this.state.variantId); return v ? (v.title || '').trim() : ''; };

  AicoPerso.prototype.setActive = function (option, node) {
    Array.prototype.forEach.call(this.root.querySelectorAll('.aico-option__value[data-option="' + option + '"]'),
      function (n) { n.classList.remove('aico-option__value--active'); });
    if (node) node.classList.add('aico-option__value--active');
  };

  AicoPerso.prototype.onOption = function (v) {
    var option = v.getAttribute('data-option');
    var id = v.getAttribute('data-id');
    this.setActive(option, v);
    if (option === 'type') { this.state.type = id; this.onProductChange(); }
    else if (option === 'gender_variant') { this.state.gender = id; this.onProductChange(); }
    else if (option === 'size') { this.state.variantId = id || null; this.setVariantInput(); this.renderSvz(); }
  };

  // type/gender change -> swap visible jersey, rebuild sizes, retitle, re-render SVZ controls
  AicoPerso.prototype.onProductChange = function (initial) {
    this.filterGenderOptions();
    var p = this.currentProduct();
    var blocks = this.root.querySelectorAll('.aico-perso__media-block');
    var shown = null;
    Array.prototype.forEach.call(blocks, function (b) {
      var match = b.getAttribute('data-type') === (p.type || '') && b.getAttribute('data-gender-variant') === (p.genderVariant || '');
      b.classList.toggle('visually-hidden', !match);
      if (match) shown = b;
    });
    if (!shown && blocks.length) {
      Array.prototype.forEach.call(blocks, function (b) {
        var m = b.getAttribute('data-type') === (p.type || '');
        b.classList.toggle('visually-hidden', !m && b !== blocks[0]);
      });
    }
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-product-title]'), function (n) { n.textContent = p.title || ''; });
    this.rebuildSizes(p);
    this.renderSvz();
  };

  AicoPerso.prototype.filterGenderOptions = function () {
    var self = this, type = this.state.type;
    var valid = this.products.filter(function (p) { return p.type === type; }).map(function (p) { return p.genderVariant; });
    var nodes = this.root.querySelectorAll('.aico-option__value[data-option="gender_variant"]');
    var validNodes = [], activeNode = null;
    Array.prototype.forEach.call(nodes, function (n) {
      var id = n.getAttribute('data-id'), ok = valid.indexOf(id) !== -1;
      n.classList.toggle('hide', !ok);
      if (ok) { validNodes.push(n); if (id === self.state.gender) activeNode = n; }
    });
    // auto-select: a single variant, or when the current one became invalid; else keep + mark the active one
    if (!activeNode || validNodes.length === 1) {
      var pick = validNodes[0];
      if (pick) { self.state.gender = pick.getAttribute('data-id'); self.setActive('gender_variant', pick); }
    } else {
      self.setActive('gender_variant', activeNode);
    }
  };

  AicoPerso.prototype.rebuildSizes = function (p) {
    var anySize = this.root.querySelector('.aico-option__value[data-option="size"]');
    var wrap = anySize && anySize.parentElement;
    if (!wrap) return;
    wrap.innerHTML = (p.variants || []).map(function (v) {
      var dis = v.available ? '' : ' aico-option__value--disabled';
      return '<div class="aico-option__value aico-js-option' + dis + '" data-id="' + v.id + '" data-option="size"' +
        (v.available ? ' tabindex="0"' : '') + '><span class="aico-option__text">' + v.title + '</span></div>';
    }).join('');
    this.state.variantId = null;
    var firstAvail = (p.variants || []).find(function (v) { return v.available; });
    if (firstAvail) {
      var node = wrap.querySelector('.aico-option__value[data-id="' + firstAvail.id + '"]');
      this.state.variantId = firstAvail.id; if (node) node.classList.add('aico-option__value--active');
    }
    this.setVariantInput();
  };

  AicoPerso.prototype.setVariantInput = function () {
    var inp = this.root.querySelector('[data-product-variant-input]');
    if (inp) inp.value = this.state.variantId || (this.currentProduct().id || '');
  };

  /* ---------------- SVZ rule derivation ---------------- */
  AicoPerso.prototype.rule = function (kind, predicate) {
    return (this.data.rules || []).filter(function (r) { return r.kind === kind; }).find(predicate);
  };
  AicoPerso.prototype.farbe = function () {
    var t = this.jersey().type;
    var r = this.rule('farbe', function (r) { return arr(r.matchType).length === 0 || listHas(r.matchType, t); });
    return r ? r.result : null;
  };
  AicoPerso.prototype.nameSize = function () {
    var c = this.jersey().variant;
    var r = this.rule('name_size', function (r) { return arr(r.matchCut).length === 0 || listHas(r.matchCut, c); });
    return r ? r.result : null;
  };
  AicoPerso.prototype.numberSize = function (chosenSize) {
    var jt = this.jersey().type, c = this.jersey().variant;
    var tokens = (chosenSize || '').split(/[\s(),\-\/]+/).filter(Boolean).map(function (t) { return t.toUpperCase(); });
    var matches = (this.data.rules || []).filter(function (r) {
      return r.kind === 'number_size' &&
             (arr(r.matchType).length === 0 || listHas(r.matchType, jt)) &&
             (arr(r.matchCut).length === 0 || listHas(r.matchCut, c)) &&
             (arr(r.sizes).length === 0 || arr(r.sizes).some(function (s) { return tokens.indexOf(String(s).toUpperCase()) !== -1; }));
    });
    if (!matches.length) return null;
    function specificity(r) {
      return (arr(r.matchType).length ? 1 : 0) + (arr(r.matchCut).length ? 1 : 0) + (arr(r.sizes).length ? 1 : 0);
    }
    matches.sort(function (a, b) { return specificity(b) - specificity(a); });
    return matches[0].result;
  };
  // badge colour: blank result = hide (wins); else most-specific match wins
  AicoPerso.prototype.badgeFarbe = function () {
    var j = this.jersey();
    var matches = (this.data.rules || []).filter(function (r) {
      return r.kind === 'badge' &&
             (arr(r.matchType).length === 0 || listHas(r.matchType, j.type)) &&
             (arr(r.matchCut).length === 0 || listHas(r.matchCut, j.variant));
    });
    if (!matches.length) return null;
    if (matches.some(function (r) { return !r.result; })) return null;
    function specificity(r) { return (arr(r.matchType).length ? 1 : 0) + (arr(r.matchCut).length ? 1 : 0); }
    matches.sort(function (a, b) { return specificity(b) - specificity(a); });
    return matches[0].result;
  };

  /* ---------------- print product classification ---------------- */
  AicoPerso.prototype.optIndex = function (print, rx) {
    var names = print.optionNames || [];
    for (var i = 0; i < names.length; i++) { if (rx.test(names[i])) return i; }
    return -1;
  };
  AicoPerso.prototype.kindOf = function (print) {
    var t = (print.optionType || '').toLowerCase();
    if (t === 'player') return 'name';
    if (t === 'custom') return 'custom';
    if (t === 'number') return 'number';
    if (t === 'badge') return 'badge';
    if (this.optIndex(print, RX.digit) >= 0) return 'number';
    if (this.optIndex(print, RX.name) >= 0) return 'name';
    return 'custom';
  };
  AicoPerso.prototype.valueIdx = function (print) {
    var vo = print.valueOption, names = print.optionNames || [];
    if (vo) { for (var i = 0; i < names.length; i++) { if (String(names[i]).toLowerCase() === String(vo).toLowerCase()) return i; } }
    var k = this.kindOf(print);
    if (k === 'name') return this.optIndex(print, RX.name);
    if (k === 'number') return this.optIndex(print, RX.digit);
    if (k === 'badge') return this.optIndex(print, RX.color);
    return -1;
  };
  AicoPerso.prototype.prints = function (kind) {
    var self = this;
    return (this.data.prints || []).filter(function (p) { return self.kindOf(p) === kind; });
  };

  /* ---------------- stock ---------------- */
  AicoPerso.prototype.skuQty = function (variantId) {
    var q = null;
    (this.data.prints || []).forEach(function (p) { (p.variants || []).forEach(function (v) { if (String(v.id) === String(variantId)) q = v.inventoryQty; }); });
    return q;
  };
  // TEMP (config.honorContinueSelling): "continue selling" SKUs treated as unlimited until SVZ stock is authoritative
  AicoPerso.prototype.continueSelling = function (variantId) {
    if (!(this.data.config && this.data.config.honorContinueSelling)) return false;
    var c = false;
    (this.data.prints || []).forEach(function (p) { (p.variants || []).forEach(function (v) { if (String(v.id) === String(variantId) && v.continueSelling) c = true; }); });
    return c;
  };
  AicoPerso.prototype.availableQty = function (variantId) {
    if (this.continueSelling(variantId)) return null; // sells past 0 -> treat as unlimited
    var inv = this.skuQty(variantId);
    if (inv == null) return null; // untracked -> unlimited
    return Math.max(0, inv - (this.inCart[variantId] || 0));
  };
  AicoPerso.prototype.digitVariant = function (print, digit) {
    return findVariant(print, [this.farbe(), this.numberSize(this.chosenSize()), digit]);
  };
  AicoPerso.prototype.isNumberAvailable = function (print, n) {
    var digits = String(n).split(''), counts = {}, self = this;
    digits.forEach(function (d) { counts[d] = (counts[d] || 0) + 1; });
    return Object.keys(counts).every(function (d) {
      var v = self.digitVariant(print, d); if (!v) return false;
      var aq = self.availableQty(v.id); return (aq == null) ? v.available : (aq >= counts[d]);
    });
  };
  AicoPerso.prototype.numberLines = function (print, n) {
    if (!print || n === '' || n == null) return null;
    if (!this.isNumberAvailable(print, n)) return null;
    var digits = String(n).split(''), counts = {}, self = this, lines = [];
    digits.forEach(function (d) { counts[d] = (counts[d] || 0) + 1; });
    Object.keys(counts).forEach(function (d) { var v = self.digitVariant(print, d); if (v) lines.push({ variantId: v.id, qty: counts[d], price: v.price }); });
    return { value: String(n), lines: lines };
  };
  AicoPerso.prototype.selectNumber = function (print, n) { this.state.number = this.numberLines(print, n); };

  AicoPerso.prototype.playersMap = function () {
    if (this._pmap) return this._pmap;
    var m = {};
    (this.data.players || []).forEach(function (p) { if (p.name != null && p.name !== '') m[p.name] = p.number; });
    this._pmap = m; return m;
  };
  AicoPerso.prototype.selectPredefinedPlayer = function (handle, nm, numberPrint) {
    this.selectName(handle, nm);
    var num = this.playersMap()[nm];
    this.state.number = (num != null && num !== '' && numberPrint) ? this.numberLines(numberPrint, num) : null;
  };
  AicoPerso.prototype.selectName = function (handle, nm) {
    var print = (this.data.prints || []).find(function (p) { return p.handle === handle; });
    if (!print) { this.state.name = null; return; }
    var v = findVariant(print, [this.farbe(), this.nameSize(), nm]);
    this.state.name = v ? { handle: handle, variantId: v.id, label: nm, price: v.price, available: v.available } : { label: nm, missing: true };
  };
  AicoPerso.prototype.onCustom = function (text, customPrint) {
    text = (text || '').trim();
    this.state.custom = text;
    if (!text) { this.state.name = null; return; }
    if (!customPrint) { this.state.name = { label: text, custom: true, propertyOnly: true }; return; }
    var v = findVariant(customPrint, [this.farbe(), this.nameSize()]);
    this.state.name = v ? { handle: customPrint.handle, variantId: v.id, label: text, price: v.price, custom: true, available: v.available } : { label: text, missing: true };
  };

  AicoPerso.prototype.printBasePrice = function (print) { var v = findVariant(print, [this.farbe(), this.nameSize()]); return v ? v.price : 0; };
  // the Fantasienamen SKU for the selected jersey — carries the flat print fee in custom mode,
  // so it goes into the bundle even when only a number is chosen (name input empty)
  AicoPerso.prototype.customVariant = function () {
    return this._customPrint ? findVariant(this._customPrint, [this.farbe(), this.nameSize()]) : null;
  };
  AicoPerso.prototype.numberBasePrice = function () {
    var np = this.prints('number')[0]; if (!np) return 0;
    var v = findVariant(np, [this.farbe(), this.numberSize(this.chosenSize())]); return v ? v.price : 0;
  };

  /* ---------------- render SVZ controls ---------------- */
  AicoPerso.prototype.renderSvz = function () {
    if (!this.controls) return;
    this.controls.innerHTML = '';
    this.state.mode = 'none'; this.state.name = null; this.state.custom = ''; this.state.number = null; this.state.badge = null;
    this.renderPrint();
    if (this._debug) this.renderDebug();
    this.update();
  };

  // debug panel (off by default; enable with window.aicoSvzDebug = true or ?aicoSvzDebug in the URL)
  AicoPerso.prototype.renderDebug = function () {
    if (!this.controls) return;
    var self = this, j = this.jersey(), L = [];
    var farbe = this.farbe(), ns = this.nameSize(), nu = this.numberSize(this.chosenSize());
    L.push('Jersey: type="' + (j.type || '') + '"  variant="' + (j.variant || '') + '"  size="' + this.chosenSize() + '"');
    L.push('Rules: ' + ((this.data.rules || []).length) + '   Prints: ' + ((this.data.prints || []).length) + '   Players: ' + ((this.data.players || []).length));
    L.push('Derived: Farbe=' + (farbe || 'NONE') + '   nameSize=' + (ns || 'NONE') + '   numberSize=' + (nu || 'NONE') + '   badge=' + (this.badgeFarbe() || 'NONE'));
    (this.data.prints || []).forEach(function (p) {
      L.push('  • ' + p.handle + '  kind=' + self.kindOf(p) + '  opts=[' + (p.optionNames || []).join(', ') + ']  variants=' + ((p.variants || []).length));
    });
    var npv = this.prints('name')[0];
    if (npv) { var bv = findVariant(npv, [farbe, ns]); L.push('Name base SKU [Farbe=' + farbe + ', size=' + ns + ']: ' + (bv ? ('qty=' + bv.inventoryQty + ' options=[' + (bv.options || []).join(', ') + ']') : 'NOT FOUND — derived Farbe/size do not match the print variant values')); }
    var nmv = this.prints('number')[0];
    if (nmv) { var bv2 = findVariant(nmv, [farbe, nu, '5']); L.push('Number digit "5" [Farbe=' + farbe + ', size=' + nu + ']: ' + (bv2 ? ('qty=' + bv2.inventoryQty) : 'NOT FOUND — check Farbe/numberSize/digit values')); }
    var box = el('details'); box.open = true;
    box.style.cssText = 'background:#111;color:#7CFC00;font:12px/1.45 monospace;padding:10px 12px;margin:10px 0;border-radius:6px;white-space:pre-wrap;word-break:break-word;';
    var sum = el('summary'); sum.textContent = 'AICO SVZ (home) debug'; sum.style.cssText = 'color:#ffd100;cursor:pointer;font-weight:700;';
    box.appendChild(sum); box.appendChild(document.createTextNode('\n' + L.join('\n')));
    this.controls.appendChild(box);
  };

  // a print is only offered if its base SKU (derived Farbe + name size) actually resolves;
  // if Farbe/size can't be derived, findVariant returns nothing → hide the option (no "+ 0.00")
  AicoPerso.prototype.hasBaseVariant = function (print) {
    return !!findVariant(print, [this.farbe(), this.nameSize()]);
  };

  AicoPerso.prototype.renderPrint = function () {
    var self = this;
    var namePrints = this.prints('name').filter(function (p) { return self.hasBaseVariant(p); });
    var customPrints = this.prints('custom').filter(function (p) { return self.hasBaseVariant(p); });
    var numberPrints = this.prints('number');
    this._numberPrint = numberPrints[0] || null;
    this._customPrint = customPrints[0] || null;
    this._namePrints = namePrints;
    if (!namePrints.length && !customPrints.length) { this.renderBadge(); return; }

    var group = el('div', 'aico-option');
    group.appendChild(el('div', 'aico-option__label')).textContent = 'Print';
    var values = el('div', 'aico-option__values');
    values.appendChild(this.chip('none', (CFG.strings && CFG.strings.none) || 'Ohne', true));
    var numBase = this.numberBasePrice();
    // one chip per name product (e.g. Spieler / Spielerinnen) — each opens its own player dropdown
    namePrints.forEach(function (np, i) {
      values.appendChild(self.chip('name:' + i, self.nameLabel(np), false, self.printBasePrice(np) + numBase));
    });
    if (customPrints.length) values.appendChild(this.chip('custom', customPrints[0].optionLabel || 'Individuell', false, this.printBasePrice(customPrints[0]) + numBase));
    group.appendChild(values);

    var fields = el('div', 'm-print');

    // a player dropdown per name product
    this._predefWraps = [];
    namePrints.forEach(function (np, i) {
      var wrap = el('div'); wrap.style.display = 'none';
      wrap.appendChild(self.buildPlayerSelect(np, numberPrints[0]));
      self._predefWraps[i] = wrap;
      fields.appendChild(wrap);
    });

    if (customPrints.length) {
      this._customWrap = el('div', 'm-print'); this._customWrap.style.display = 'none';
      var cp = customPrints[0];
      var nameBox = el('div', 'm-print__fieldset');
      var nameI = el('input', 'm-print__field a-input__field a-input__field--full-width');
      nameI.type = 'text'; nameI.maxLength = (CFG.maxCharName || 14); nameI.placeholder = 'Name';
      nameI.addEventListener('input', function () { self.onCustom(nameI.value, cp); self.update(); });
      this._customNameInput = nameI;
      nameBox.appendChild(nameI);
      var info = el('span', 'm-print__info');
      info.textContent = (CFG.strings && CFG.strings.maxCharsName) || ('max. ' + (CFG.maxCharName || 14) + ' Zeichen');
      nameBox.appendChild(info);
      this._customWrap.appendChild(nameBox);
      if (numberPrints.length) {
        // leading "Ohne" entry so a picked number can be cleared again (name-only print)
        var nip = numberPrints[0], numItems = [{ value: '', label: (CFG.strings && CFG.strings.none) || 'Ohne' }];
        for (var n = 0; n <= 99; n++) numItems.push({ value: String(n), label: String(n), disabled: !self.isNumberAvailable(nip, n) });
        this._customWrap.appendChild(this.tomSelect(numItems, 'Nummer', function (val) { self.selectNumber(nip, val); self.update(); }));
      }
      fields.appendChild(this._customWrap);
    }

    group.appendChild(fields);
    this.controls.appendChild(group);
    this.renderBadge();
  };

  // chip label for a name product: aico.option_label if set, else a sensible default (men/women)
  AicoPerso.prototype.nameLabel = function (np) {
    if (np.optionLabel) return np.optionLabel;
    return /spielerinn/i.test((np.handle || '') + ' ' + (np.title || '')) ? 'Spielerinnen' : 'Spieler';
  };

  // build the player dropdown for one name product
  AicoPerso.prototype.buildPlayerSelect = function (np, numberPrint) {
    var self = this, ni = this.valueIdx(np), names = [], pmap = this.playersMap();
    (np.variants || []).forEach(function (v) { var nm = v.options[ni]; if (nm && names.indexOf(nm) === -1) names.push(nm); });
    var items = names
      .filter(function (nm) { var num = pmap[nm]; return num != null && num !== ''; }) // skip players missing from the players metaobject (no number → can't resolve stock)
      .map(function (nm) {
        var nameVar = findVariant(np, [self.farbe(), self.nameSize(), nm]);
        var num = pmap[nm], nameOk = false;
        if (nameVar) { var aqn = self.availableQty(nameVar.id); nameOk = (aqn == null) ? nameVar.available : (aqn >= 1); }
        var numOk = !!(numberPrint && self.isNumberAvailable(numberPrint, num));
        return { value: nm, label: num + ' - ' + nm, disabled: !(nameOk && numOk) };
      });
    return this.tomSelect(items, (CFG.strings && CFG.strings.selectPlayer) || 'Spieler:in wählen', function (val) {
      self.state.custom = '';
      if (val) self.selectPredefinedPlayer(np.handle, val, numberPrint); else { self.state.name = null; self.state.number = null; }
      self.update();
    });
  };

  AicoPerso.prototype.chip = function (mode, label, empty, price) {
    var self = this;
    var c = el('div', 'aico-option__value' + (empty ? ' aico-option__value--has-image aico-option--empty' : ''));
    c.setAttribute('data-print-mode', mode); c.setAttribute('tabindex', '0');
    var t = el('span', 'aico-option__text');
    if (empty) t.innerHTML = DIVIDE_ICON + ' ' + label; else t.textContent = label;
    c.appendChild(t);
    if (!empty) { var p = el('span', 'aico-option__price'); p.textContent = '+ ' + formatMoney(price || 0); c.appendChild(p); }
    function pick() { self.setPrintMode(mode, c); }
    c.addEventListener('click', pick);
    c.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
    return c;
  };

  AicoPerso.prototype.setPrintMode = function (mode, chip) {
    Array.prototype.forEach.call(this.controls.querySelectorAll('.aico-option__value[data-print-mode]'), function (n) { n.classList.remove('aico-option__value--active'); });
    if (chip) chip.classList.add('aico-option__value--active');
    this.state.mode = mode;
    this.state.name = null; this.state.number = null; this.state.custom = '';
    this.resetControls();
    var nameIdx = (mode.indexOf('name:') === 0) ? parseInt(mode.slice(5), 10) : -1;
    this._activeNamePrint = (nameIdx >= 0 && this._namePrints) ? this._namePrints[nameIdx] : null;
    (this._predefWraps || []).forEach(function (w, i) { if (w) w.style.display = (i === nameIdx) ? '' : 'none'; });
    if (this._customWrap) this._customWrap.style.display = (mode === 'custom') ? '' : 'none';
    if (nameIdx >= 0 || mode === 'custom') this.showBack();
    this.updateCustomNameEnabled();
    this.update();
  };

  AicoPerso.prototype.resetControls = function () {
    (this._predefWraps || []).concat([this._customWrap]).forEach(function (w) {
      if (!w) return;
      Array.prototype.forEach.call(w.querySelectorAll('.ts-wrapper'), function (ts) { if (ts.__reset) ts.__reset(); });
    });
    if (this._customNameInput) this._customNameInput.value = '';
  };
  // enable the custom name input whenever the Fantasienamen SKU is in stock
  // (name and number are independently selectable — no cross-gating)
  AicoPerso.prototype.updateCustomNameEnabled = function () {
    if (!this._customNameInput) return;
    var cv = this.customVariant();
    var aq = cv ? this.availableQty(cv.id) : null;
    var nameInStock = cv ? ((aq == null) ? cv.available : (aq >= 1)) : false;
    var off = !nameInStock;
    this._customNameInput.disabled = off;
    this._customNameInput.style.cursor = off ? 'not-allowed' : '';
  };

  // minimal TomSelect-style dropdown (same .ts-* markup the section's scoped CSS expects)
  AicoPerso.prototype.tomSelect = function (items, placeholder, onChange) {
    var wrap = el('div', 'ts-wrapper single');
    var control = el('div', 'ts-control'); control.setAttribute('role', 'combobox'); control.setAttribute('tabindex', '0');
    var dd = el('div', 'ts-dropdown single'); dd.style.display = 'none';
    var search = null, optionEls = [];
    if (items.length > 10) {
      search = el('input', 'ts-search'); search.type = 'text';
      search.placeholder = (CFG.strings && CFG.strings.search) || 'Suchen…';
      search.addEventListener('click', function (e) { e.stopPropagation(); });
      search.addEventListener('input', function () {
        var q = search.value.trim().toLowerCase();
        optionEls.forEach(function (o) { o.style.display = o.textContent.toLowerCase().indexOf(q) !== -1 ? '' : 'none'; });
      });
      dd.appendChild(search);
    }
    var content = el('div', 'ts-dropdown-content'); dd.appendChild(content);
    function setLabel(t) { control.innerHTML = ''; control.appendChild(el('div', 'item')).textContent = t; }
    function open() {
      dd.style.display = 'block'; wrap.classList.add('dropdown-active', 'focus');
      if (search) { search.value = ''; optionEls.forEach(function (o) { o.style.display = ''; }); setTimeout(function () { search.focus(); }, 0); }
    }
    function close() { dd.style.display = 'none'; wrap.classList.remove('dropdown-active', 'focus'); }
    setLabel(placeholder);
    var ph = el('div', 'option'); ph.setAttribute('role', 'option'); ph.setAttribute('aria-disabled', 'true'); ph.textContent = placeholder; content.appendChild(ph);
    items.forEach(function (it) {
      var o = el('div', 'option'); o.setAttribute('role', 'option'); o.setAttribute('data-value', it.value); o.textContent = it.label; optionEls.push(o);
      if (it.disabled) { o.setAttribute('aria-disabled', 'true'); o.style.opacity = '.4'; o.style.textDecoration = 'line-through'; }
      else { o.setAttribute('data-selectable', ''); }
      o.addEventListener('click', function () {
        if (it.disabled) return;
        Array.prototype.forEach.call(content.children, function (c) { c.classList.remove('active', 'selected'); });
        o.classList.add('active', 'selected'); setLabel(it.label); wrap.classList.add('has-items'); close();
        onChange(it.value);
      });
      content.appendChild(o);
    });
    control.addEventListener('click', function () { dd.style.display === 'block' ? close() : open(); });
    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });
    wrap.__reset = function () {
      setLabel(placeholder);
      Array.prototype.forEach.call(content.children, function (c) { c.classList.remove('active', 'selected'); });
      wrap.classList.remove('has-items', 'dropdown-active', 'focus'); dd.style.display = 'none';
    };
    wrap.appendChild(control); wrap.appendChild(dd);
    return wrap;
  };

  /* badge: derived colour per jersey -> Ohne + that one colour (none if the jersey gets no badge) */
  // colour badge if it has >1 variant, else a single-variant badge added as-is (value_option ignored)
  AicoPerso.prototype.badgeHasVariants = function (bp) { return (bp.variants || []).length > 1; };

  // resolve all badge products: variant badges pick by rule, single-variant added as-is, null = no badge
  AicoPerso.prototype.resolveBadges = function () {
    var self = this, bps = this.prints('badge');
    if (!bps.length) return null;
    var derived = this.badgeFarbe();
    if (!derived) return null;
    var items = [], ok = true, price = 0, plainImage = null, colourImage = null, variantLabel = null, companionLabel = null;
    bps.forEach(function (bp) {
      var variant;
      if (self.badgeHasVariants(bp)) {
        var vi = self.valueIdx(bp);
        variant = (bp.variants || []).find(function (v) { return String((v.options || [])[vi]) === String(derived); });
        if (!variantLabel) variantLabel = bp.optionLabel;
        if (variant && variant.image) colourImage = variant.image;
      } else {
        variant = (bp.variants || [])[0];
        if (!companionLabel) companionLabel = bp.optionLabel;
        if (variant && variant.image) plainImage = variant.image;
      }
      if (!variant) { ok = false; return; }
      var aq = self.availableQty(variant.id);
      if (!((aq == null) ? variant.available : (aq >= 1))) ok = false; // all-or-nothing stock
      price += (variant.price || 0);
      items.push({ variantId: variant.id, price: variant.price || 0 });
    });
    return {
      items: items, price: price, colour: derived,
      label: variantLabel || companionLabel || derived || 'Badge', // option_label (variant badge -> other badge) -> resolved variant name -> "Badge"
      available: ok && items.length > 0, plainImage: plainImage, colourImage: colourImage
    };
  };

  AicoPerso.prototype.renderBadge = function () {
    var rb = this.resolveBadges();
    if (!rb) return;
    var group = el('div', 'aico-option');
    group.appendChild(el('div', 'aico-option__label')).textContent = rb.label;
    var values = el('div', 'aico-option__values'); values.setAttribute('data-full-width', '');
    values.appendChild(this.badgeChip(null, rb));
    values.appendChild(this.badgeChip(rb, rb));
    group.appendChild(values);
    this.controls.appendChild(group);
  };

  AicoPerso.prototype.badgeChip = function (sel, rb) {
    var self = this;
    if (!sel) {
      var c0 = el('div', 'aico-option__value aico-option__value--has-image aico-option__value--full-width aico-option--empty');
      c0.setAttribute('data-option', 'badge'); c0.setAttribute('tabindex', '0');
      var t0 = el('span', 'aico-option__text'); t0.innerHTML = DIVIDE_ICON + ' ' + ((CFG.strings && CFG.strings.none) || 'Ohne'); c0.appendChild(t0);
      c0.addEventListener('click', function () { self.selectBadge(null, c0); });
      return c0;
    }
    var disabled = !rb.available;
    var c = el('div', 'aico-option__value aico-option__value--has-image aico-option__value--full-width' + (disabled ? ' aico-option__value--disabled' : ''));
    c.setAttribute('data-option', 'badge'); if (!disabled) c.setAttribute('tabindex', '0');
    var t = el('span', 'aico-option__text');
    var img = rb.colourImage;
    if (img) t.innerHTML = '<img class="aico-option__image" src="' + img + '" alt="">';
    t.appendChild(document.createTextNode(rb.label));
    c.appendChild(t);
    if (rb.price) { var p = el('span', 'aico-option__price'); p.textContent = '+ ' + formatMoney(rb.price); c.appendChild(p); }
    if (!disabled) c.addEventListener('click', function () { self.selectBadge(rb, c); });
    return c;
  };

  AicoPerso.prototype.selectBadge = function (rb, chip) {
    Array.prototype.forEach.call(this.controls.querySelectorAll('.aico-option__value[data-option="badge"]'), function (n) { n.classList.remove('aico-option__value--active'); });
    if (chip) chip.classList.add('aico-option__value--active');
    this.state.badge = rb ? { items: rb.items, price: rb.price, label: rb.colour, plainImage: rb.plainImage, colourImage: rb.colourImage } : null;
    this.update();
  };

  /* ---------------- preview ---------------- */
  AicoPerso.prototype.activeBlock = function () {
    return this.root.querySelector('.aico-perso__media-block:not(.visually-hidden)') || this.root.querySelector('.aico-perso__media-block');
  };
  AicoPerso.prototype.flip = function () {
    Array.prototype.forEach.call(this.root.querySelectorAll('.aico-perso__media-item'), function (it) { it.classList.toggle('active'); });
  };
  AicoPerso.prototype.showBack = function () {
    Array.prototype.forEach.call(this.root.querySelectorAll('.aico-perso__media-item--front'), function (n) { n.classList.remove('active'); });
    Array.prototype.forEach.call(this.root.querySelectorAll('.aico-perso__media-item--back'), function (n) { n.classList.add('active'); });
  };
  AicoPerso.prototype.showFront = function () {
    Array.prototype.forEach.call(this.root.querySelectorAll('.aico-perso__media-item--front'), function (n) { n.classList.add('active'); });
    Array.prototype.forEach.call(this.root.querySelectorAll('.aico-perso__media-item--back'), function (n) { n.classList.remove('active'); });
  };

  AicoPerso.prototype.update = function () {
    var block = this.activeBlock();
    if (block) {
      var nm = this.state.name ? this.state.name.label : (this.state.custom || '');
      Array.prototype.forEach.call(block.querySelectorAll('.aico-perso__name'), function (n) { n.textContent = nm || ''; });
      var num = this.state.number ? this.state.number.value : '';
      Array.prototype.forEach.call(block.querySelectorAll('.aico-perso__number'), function (n) {
        n.innerHTML = '';
        if (num !== '') {
          var base = (CFG.fileUrl || ''), suffix = n.getAttribute('data-image-suffix') || '', ext = n.getAttribute('data-image-extension') || 'png';
          // support both file naming conventions: unpadded (1-) and zero-padded to 2 digits (01-)
          var cands = [base + num + '-' + suffix + '.' + ext, base + ('0' + num).slice(-2) + '-' + suffix + '.' + ext];
          if (cands[1] === cands[0]) cands.length = 1;
          var img = document.createElement('img'); img.className = 'aico-perso__number-image'; img.alt = '';
          var i = 0; img.onerror = function () { if (++i < cands.length) img.src = cands[i]; else img.style.display = 'none'; };
          img.src = cands[0];
          n.appendChild(img);
        }
      });
      var badge = this.state.badge;
      Array.prototype.forEach.call(block.querySelectorAll('.aico-perso__badge'), function (b) {
        b.classList.remove('aico-perso__badge--left', 'aico-perso__badge--right');
        if (badge && (badge.plainImage || badge.colourImage)) {
          var both = badge.plainImage && badge.colourImage;
          var html = '<span class="aico-perso__badge-combo' + (both ? ' aico-perso__badge-combo--two' : '') + '">';
          if (badge.plainImage)  html += '<img class="aico-perso__badge-image aico-perso__badge-mini--tl" src="' + badge.plainImage + '" alt="">';
          if (badge.colourImage) html += '<img class="aico-perso__badge-image aico-perso__badge-mini--br" src="' + badge.colourImage + '" alt="">';
          html += '</span>';
          b.innerHTML = html;
          b.classList.add(b.hasAttribute('data-front') ? 'aico-perso__badge--left' : 'aico-perso__badge--right');
        } else b.innerHTML = '';
      });
    }
    this.updatePrice();
    this.validate();
  };

  /* ---------------- price ---------------- */
  AicoPerso.prototype.basePrice = function () {
    var v = this.variantById(this.state.variantId);
    if (v) return v.price;
    var p = this.currentProduct(); return (p.variants && p.variants[0] && p.variants[0].price) || p.price || 0;
  };
AicoPerso.prototype.optionsPrice = function () {
  var s = this.state, total = 0;
  var isName = s.mode.indexOf('name:') === 0, isCustom = s.mode === 'custom';
  if (isName || isCustom) {
    var nameP = isName ? this._activeNamePrint : this.prints('custom')[0];
    // sobald ein/e konkrete/r Spieler:in gewählt ist: dessen ECHTEN Preis nehmen
    // (kann 0 sein bei Gratisflock) — vorher weiter den Platzhalter-Preis zeigen
    if (s.name && !s.name.missing && typeof s.name.price === 'number') {
      total += s.name.price;
    } else {
      total += nameP ? this.printBasePrice(nameP) : 0;
    }
    if (s.number && s.number.lines && s.number.lines.length) s.number.lines.forEach(function (l) { total += (l.price || 0) * l.qty; });
    else total += this.numberBasePrice();
  }
  if (s.badge && s.badge.price) total += s.badge.price;
  return total;
};
  AicoPerso.prototype.updatePrice = function () {
    var base = this.basePrice(), opt = this.optionsPrice();
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-product-price]'), function (n) { n.textContent = formatMoney(base); });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-options-price]'), function (n) { n.textContent = opt > 0 ? ' + ' + formatMoney(opt) : ''; });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-total-price]'), function (n) { n.textContent = formatMoney(base + opt); });
  };

  /* ---------------- validation ---------------- */
  AicoPerso.prototype.validate = function () {
    var s = this.state, ok = !!s.variantId;
    if (s.mode.indexOf('name:') === 0) ok = ok && !!(s.name && s.name.variantId);
    else if (s.mode === 'custom') {
      // name only, number only, or both — at least one (flat print fee either way)
      var hasName = !!(s.name && (s.name.variantId || s.name.propertyOnly));
      var hasNumber = !!(s.number && s.number.lines && s.number.lines.length);
      ok = ok && (hasName || hasNumber);
    }
    if (s.name && s.name.missing) ok = false;
    var btn = this.root.querySelector('[data-btn-submit]');
    if (btn) btn.disabled = !ok;
    return ok;
  };

  /* ---------------- quantity cap ---------------- */
  AicoPerso.prototype.maxBundleQty = function () {
    var max = Infinity, s = this.state, self = this;
    if (s.name && s.name.variantId) { var nq = self.availableQty(s.name.variantId); if (nq != null) max = Math.min(max, nq); }
    else if (s.mode === 'custom') { // number-only still adds the fee SKU on submit — count its stock too
      var cv = this.customVariant();
      if (cv) { var cq = self.availableQty(cv.id); if (cq != null) max = Math.min(max, cq); }
    }
    if (s.number && s.number.lines) s.number.lines.forEach(function (l) { var q = self.availableQty(l.variantId); if (q != null) max = Math.min(max, Math.floor(q / l.qty)); });
    if (s.badge && s.badge.items) s.badge.items.forEach(function (it) { var bq = self.availableQty(it.variantId); if (bq != null) max = Math.min(max, bq); });
    return max;
  };

  /* Render the configured jersey (active media view: base image + name + number + badge) onto a
     canvas and return a small JPEG data URL — or null on any failure (never blocks add-to-cart). */
  AicoPerso.prototype.captureImage = function (block, pfx) {
    return new Promise(function (resolve) {
      var done = false; function finish(v) { if (!done) { done = true; resolve(v); } }
      setTimeout(function () { finish(null); }, 8000);
      try {
        if (!block) return finish(null);
        var active = block.querySelector('.' + pfx + '__media-item.active') || block.querySelector('.' + pfx + '__media-item') || block;
        var baseEl = null, imgs = active.querySelectorAll('img');
        for (var i = 0; i < imgs.length; i++) { if (!imgs[i].closest('.' + pfx + '__number') && !imgs[i].closest('.' + pfx + '__badge')) { baseEl = imgs[i]; break; } }
        var measureEl = baseEl || active, baseRect = measureEl.getBoundingClientRect();
        if (!baseRect.width) return finish(null);
        var baseSrc = baseEl ? (baseEl.currentSrc || baseEl.src) : null;
        if (!baseSrc) { var bg = window.getComputedStyle(active).backgroundImage, m = bg && bg.match(/url\(["']?(.*?)["']?\)/); baseSrc = m ? m[1] : null; }
        if (!baseSrc) return finish(null);
        var nameEl = active.querySelector('.' + pfx + '__name');
        var numImg = active.querySelector('.' + pfx + '__number img');
        var badgeEls = Array.prototype.slice.call(active.querySelectorAll('.' + pfx + '__badge img'));
        function load(src) { return new Promise(function (res) { if (!src) return res(null); var im = new Image(); im.crossOrigin = 'anonymous'; im.onload = function () { res(im); }; im.onerror = function () { res(null); }; im.src = src; }); }
        var fontsReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
        Promise.all([load(baseSrc), load(numImg && (numImg.currentSrc || numImg.src)), fontsReady].concat(badgeEls.map(function (be) { return load(be.currentSrc || be.src); }))).then(function (r) {
          var base = r[0], num = r[1], badgeImgs = r.slice(3); // r[2] = fonts
          if (!base) return finish(null);
          var bw = base.naturalWidth || baseRect.width, bh = base.naturalHeight || baseRect.height;
          var scale = Math.min(1, 600 / bw), cw = Math.round(bw * scale), ch = Math.round(bh * scale);
          var canvas = document.createElement('canvas'); canvas.width = cw; canvas.height = ch;
          var ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cw, ch); // white bg (JPEG has no transparency → avoids black where the jersey PNG is transparent)
          ctx.drawImage(base, 0, 0, cw, ch);
          var sx = cw / baseRect.width, sy = ch / baseRect.height;
          function rectOf(el) { var rr = el.getBoundingClientRect(); return { x: (rr.left - baseRect.left) * sx, y: (rr.top - baseRect.top) * sy, w: rr.width * sx, h: rr.height * sy }; }
          if (num && numImg) { var rn = rectOf(numImg); ctx.drawImage(num, rn.x, rn.y, rn.w, rn.h); }
          badgeEls.forEach(function (be, bi) { var bImg = badgeImgs[bi]; if (bImg) { var rbb = rectOf(be); ctx.drawImage(bImg, rbb.x, rbb.y, rbb.w, rbb.h); } });
          if (badgeEls.length >= 2 && badgeEls[0].parentNode) { // short black diagonal divider between the two mini-badges
            var rc = rectOf(badgeEls[0].parentNode), cx = rc.x + rc.w / 2, cy = rc.y + rc.h / 2, h = Math.min(rc.w, rc.h) * 0.9 / 2 * Math.SQRT1_2;
            ctx.strokeStyle = '#000000'; ctx.lineWidth = Math.max(1, sy);
            ctx.beginPath(); ctx.moveTo(cx - h, cy + h); ctx.lineTo(cx + h, cy - h); ctx.stroke();
          }
          if (nameEl && nameEl.textContent && nameEl.textContent.trim()) {
            var cs = window.getComputedStyle(nameEl), rt = rectOf(nameEl);
            ctx.font = (cs.fontStyle || 'normal') + ' ' + (cs.fontWeight || 'normal') + ' ' + ((parseFloat(cs.fontSize) || 16) * sy) + 'px ' + (cs.fontFamily || 'sans-serif');
            ctx.fillStyle = cs.color || '#000'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(nameEl.textContent.trim(), rt.x + rt.w / 2, rt.y + rt.h / 2);
          }
          try { canvas.toBlob(function (b) { finish(b); }, 'image/png'); } catch (e) { finish(null); }
        }).catch(function () { finish(null); });
      } catch (e) { finish(null); }
    });
  };

  /* ---------------- add to cart (SVZ bundle) ---------------- */
  AicoPerso.prototype.onSubmit = function (e) {
    e.preventDefault();
    if (!this.validate()) return;
    var fd = new FormData(this.form);
    var jerseyId = this.state.variantId || fd.get('id');
    var qty = parseInt(fd.get('quantity'), 10) || 1;
    var maxQ = this.maxBundleQty();
    if (maxQ !== Infinity && qty > maxQ) qty = Math.max(1, maxQ);
    var bundleId = Date.now();
    var parent = { _bundleId: bundleId, _bundleRole: 'parent', _bundleEngine: 'svz' };
    var children = [];

    if (this.state.name && (this.state.name.variantId || this.state.name.propertyOnly)) {
      parent._pName = this.state.name.label;
    }
    var nameChildId = (this.state.name && this.state.name.variantId) ? this.state.name.variantId : null;
    if (!nameChildId && this.state.mode === 'custom') {
      var cv = this.customVariant(); // number-only: the Fantasienamen SKU still rides along as the flat print fee
      if (cv) nameChildId = cv.id;
    }
    if (nameChildId) {
      children.push({ id: nameChildId, quantity: qty, properties: { _bundleId: bundleId, _bundleRole: 'child' } });
    }
    if (this.state.number && this.state.number.lines) {
      parent._pNumber = this.state.number.value;
      this.state.number.lines.forEach(function (ln) {
        children.push({ id: ln.variantId, quantity: ln.qty * qty, properties: { _bundleId: bundleId, _bundleRole: 'child' } });
      });
    }
    if (this.state.badge && this.state.badge.items && this.state.badge.items.length) {
      parent._pBadge = this.state.badge.label;
      this.state.badge.items.forEach(function (it) {
        children.push({ id: it.variantId, quantity: qty, properties: { _bundleId: bundleId, _bundleRole: 'child' } });
      });
    }

    var items = children.concat([{ id: jerseyId, quantity: qty, properties: parent }]);
    var self = this, btn = this.root.querySelector('[data-btn-submit]');
    var loader = btn && btn.querySelector('.m-product-form__loader');
    if (btn) btn.classList.add('m-product-form__submit--loading');
    if (loader) loader.classList.remove('hide');

    function post(blob) {
    // multipart so the configured-jersey image rides as a native cart file-upload on the parent line
    // (Shopify hosts it and stores the CDN URL as the _trikotImage property — clickable in the order, not base64)
    var fd = new FormData();
    items.forEach(function (it, i) {
      fd.append('items[' + i + '][id]', it.id);
      fd.append('items[' + i + '][quantity]', it.quantity);
      var props = it.properties || {};
      Object.keys(props).forEach(function (k) { fd.append('items[' + i + '][properties][' + k + ']', props[k]); });
    });
    if (blob) fd.append('items[' + (items.length - 1) + '][properties][_trikotImage]', blob, 'trikot.png'); // parent jersey = last item
    var addRel = CFG.cartAddUrl || (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    var addUrl = /^https?:/.test(addRel) ? addRel : (window.location.origin + addRel); // absolute: iframe fetch has no usable relative base
    nativeFetch()(addUrl, {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, // no Content-Type: browser sets the multipart boundary
      body: fd
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) { console.warn('[aico-perso]', res.description || res.message); self.loadCart(function () { self.renderSvz(); }); return; }
        // no redirect: bump the header count + flash the center "added" overlay (like the theme/PDP)
        var added = (res.items && res.items.length) ? res.items.reduce(function (a, t) { return a + (parseInt(t.quantity, 10) || 0); }, 0) : 1;
        var cc = document.getElementById('CartCount');
        if (cc) cc.innerText = (parseInt(cc.innerText, 10) || 0) + added;
        var ov = document.getElementById('js_pagewide_overlay');
        if (ov) {
          ov.style.visibility = 'visible'; ov.style.opacity = '1';
          setTimeout(function () { ov.style.opacity = '0'; }, 2000);
          setTimeout(function () { ov.style.visibility = 'hidden'; }, 2600);
        }
        if (window.WhatSub && window.WhatSub.dispatch) window.WhatSub.dispatch('PRODUCT_FORM_ADD_TO_CART', self.form);
        document.dispatchEvent(new CustomEvent('aico:perso:added', { detail: res, bubbles: true }));
        self.loadCart(function () { self.renderSvz(); }); // reset + re-evaluate stock against the updated cart
      })
      .catch(function (err) { console.error('[aico-perso]', err); })
      .finally(function () {
        if (btn) btn.classList.remove('m-product-form__submit--loading');
        if (loader) loader.classList.add('hide');
        self.validate();
      });
    }

    // attach the configured-jersey image only when something was personalised (name/number/badge);
    // a plain jersey with no add-on doesn't need it
    if (this.state.name || this.state.number || this.state.badge) {
      var imgBlock = this.activeBlock();
      this.captureImage(imgBlock, 'aico-perso').then(function (blob) {
        if (!blob && imgBlock) {
          var active = imgBlock.querySelector('.aico-perso__media-item.active') || imgBlock.querySelector('.aico-perso__media-item') || imgBlock;
          var imgs = active.querySelectorAll('img');
          for (var i = 0; i < imgs.length; i++) {
            if (!imgs[i].closest('.aico-perso__number') && !imgs[i].closest('.aico-perso__badge')) {
              var src = imgs[i].currentSrc || imgs[i].src;
              if (src) { items[items.length - 1].properties._trikotImage = src; }
              break;
            }
          }
          items[items.length - 1].properties._trikotImageFallback = '1';
        }
        if (!blob && !imgBlock) {
          items[items.length - 1].properties._trikotImageFallback = '1';
        }
        post(blob);
      });
    } else {
      post(null);
    }
  };

  /* ---------------- boot ---------------- */
  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-aico-perso]'), function (root) {
      if (root.__aicoPerso) return;
      try { root.__aicoPerso = new AicoPerso(root); } catch (e) { console.error('[aico-perso] init failed', e); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  window.AicoPerso = AicoPerso;
})();
